import React from 'react';
import { twMerge } from 'tailwind-merge';

export interface BadgeProps {
  variant?: 'default' | 'paid' | 'premium' | 'success' | 'warning' | 'danger' | 'info' | 'collection-equipment' | 'collection-underwear' | 'count';
  size?: 'sm' | 'md';
  /** Ignored when variant="count" — count mode renders `count` instead. */
  children?: React.ReactNode;
  className?: string;
  title?: string;
  'aria-label'?: string;
  'data-testid'?: string;
  /** Escape hatch for callers driven by a dynamic color source (e.g. per-status
   * theme tokens) that doesn't map onto the fixed variant set. Overrides the
   * variant's background/text color; other variant classes (padding, shape)
   * still apply. */
  style?: React.CSSProperties;
  /** variant="count" only. Renders nothing (`null`) when 0/undefined — the
   * "don't show a bare 0" rule lives here, not as a convention every call
   * site has to remember. */
  count?: number;
  /** variant="count" only. Caps the displayed number, e.g. 99 → "99+". Default 99. */
  max?: number;
}

const variantClasses: Record<Exclude<NonNullable<BadgeProps['variant']>, 'count'>, string> = {
  default: 'bg-border-light text-text-secondary',
  paid: 'bg-gold/15 text-gold',
  premium: 'bg-crimson/15 text-crimson',
  success: 'bg-success/15 text-success',
  warning: 'bg-warning/15 text-warning',
  danger: 'bg-danger/15 text-danger',
  info: 'bg-info/15 text-info',
  'collection-equipment': 'bg-[var(--theme-collection-equipment-bg)] text-[var(--theme-collection-equipment-text)]',
  'collection-underwear': 'bg-[var(--theme-collection-underwear-bg)] text-[var(--theme-collection-underwear-text)]',
};

const sizeClasses: Record<NonNullable<BadgeProps['size']>, string> = {
  sm: 'text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider',
  md: 'text-xs px-2 py-0.5 rounded font-semibold',
};

export function Badge({
  variant = 'default',
  size = 'md',
  className = '',
  title,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
  style,
  children,
  count,
  max = 99,
}: BadgeProps) {
  if (variant === 'count') {
    if (!count || count <= 0) return null;
    const display = count > max ? `${max}+` : String(count);
    return (
      <span
        title={title}
        aria-label={ariaLabel ?? `${count} unread`}
        data-testid={dataTestId}
        style={style}
        className={twMerge(
          'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full',
          'text-[11px] font-bold font-mono leading-none',
          'bg-[var(--theme-primary)] text-[var(--theme-text-on-accent)]',
          className,
        )}
      >
        {display}
      </span>
    );
  }

  return (
    <span
      title={title}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      style={style}
      className={twMerge(
        'inline-flex items-center whitespace-nowrap leading-none',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
    >
      {children}
    </span>
  );
}
